import { getFilteredOptions } from '@/features/vehiclesearch/services';
import type {
    VehicleSearchFilters,
    VehicleDerivative,
    FilteredOptionsResponse,
} from '@/features/vehiclesearch/types/manualsearch.types';
import type { DerivativeNameSummary, VehicleNameSummary } from '@/features/vehiclesearch/services/lookup';
import type { SelectFilterOption } from '@/features/vehiclesearch/types/manualsearch.types';
import type { PlanVehicleRuleFormInput } from '../schemas/planVehicleRule.schema';
import type { PlanVehicleRule } from '../types/planVehicleRule.types';
import { fromRequires4x4 } from '../utils/fourByFourChoice';

export interface PlanVehicleRuleSeed {
    defaultValues: PlanVehicleRuleFormInput;
    filters: VehicleSearchFilters;
}

export const OPTIONS_ERROR_MESSAGE = 'Could not load vehicle options. The lists shown may be incomplete or out of date.';

export const emptyState: PlanVehicleRuleSeed = {
    defaultValues: {
        make_id: '',
        model_id: '',
        derivative_id: '',
        transmission_id: '',
        fuel_type_id: '',
        engine_min: '',
        engine_max: '',
        age_min: '',
        age_max: '',
        requires_4x4: '',
    },
    filters: {},
};

/**
 * Seeds RHF defaults and the cascade filters from a saved rule, synchronously — both must be
 * available before first render so the mount effect (which writes filters back into the form)
 * never overwrites a prefilled value with '' (the ordering trap, design section 5.2).
 *
 * A derivative rule has model_id nulled by the backend normalise(); recover it from the widened
 * lookup into filters only, matching the create flow's retained navigation state — the RHF value
 * stays null so the submitted payload is unaffected.
 */
export function buildEditState(
    rule: PlanVehicleRule,
    derivative?: DerivativeNameSummary,
): PlanVehicleRuleSeed {
    const modelId = rule.model_id ?? (rule.derivative_id ? derivative?.vehicle_model_id : undefined);

    return {
        defaultValues: {
            make_id:         rule.make_id ?? '',
            model_id:        rule.model_id ?? '',
            derivative_id:   rule.derivative_id ?? '',
            transmission_id: rule.transmission_id ?? '',
            fuel_type_id:    rule.fuel_type_id ?? '',
            engine_min:      rule.engine_min?.toString() ?? '',
            engine_max:      rule.engine_max?.toString() ?? '',
            age_min:         rule.age_min?.toString() ?? '',
            age_max:         rule.age_max?.toString() ?? '',
            requires_4x4:    fromRequires4x4(rule.requires_4x4),
        },
        filters: {
            // Null make seeds no make key: the exact no-make state the create flow produces,
            // so gating, the cascade and hasMake all behave without special-casing.
            ...(rule.make_id ? { make_id: rule.make_id } : {}),
            ...(modelId ? { model_id: modelId } : {}),
            ...(rule.transmission_id ? { transmission_id: rule.transmission_id } : {}),
            ...(rule.fuel_type_id ? { fuel_type_id: rule.fuel_type_id } : {}),
        },
    };
}

/** Merges only the keys a response actually carries, so a narrower later response cannot blank a
 * list an earlier response populated. Shared by the mount cascade and handleFilterChange. */
export function mergeAvailableOptions(
    prev: FilteredOptionsResponse['available_options'],
    next: FilteredOptionsResponse['available_options'],
): FilteredOptionsResponse['available_options'] {
    const merged = { ...prev };
    if (next.makes) merged.makes = next.makes;
    if (next.models) merged.models = next.models;
    if (next.engine_sizes) merged.engine_sizes = next.engine_sizes;
    if (next.transmissions) merged.transmissions = next.transmissions;
    if (next.fuel_types) merged.fuel_types = next.fuel_types;
    if (next.years) merged.years = next.years;
    return merged;
}

/**
 * Replays the create flow's cascade of /filtered-options calls on form open, because the endpoint
 * omits any list whose filter is already set (design "Design gap 1"). Create mode degenerates to
 * the existing single call. Calls are issued together and folded in fixed A-B-D order so the
 * result does not depend on resolution order.
 */
export async function loadCascade(
    seedFilters: VehicleSearchFilters,
    mode: 'create' | 'edit',
): Promise<{ options: FilteredOptionsResponse['available_options']; derivatives: VehicleDerivative[] }> {
    const isEdit = mode === 'edit';

    const callA = getFilteredOptions({});
    const callB = isEdit && seedFilters.make_id
        ? getFilteredOptions({ make_id: seedFilters.make_id })
        : null;
    const callD = isEdit && seedFilters.model_id
        ? getFilteredOptions(seedFilters, { includeDerivatives: true })
        : null;

    const [responseA, responseB, responseD] = await Promise.all([callA, callB, callD]);

    let options = mergeAvailableOptions({}, responseA.available_options);
    if (responseB) options = mergeAvailableOptions(options, responseB.available_options);
    if (responseD) options = mergeAvailableOptions(options, responseD.available_options);

    return {
        options,
        derivatives: responseD?.matching_vehicles ?? [],
    };
}

export function toSelectOptions(items: VehicleNameSummary[] | undefined): SelectFilterOption[] {
    return (items ?? []).map((item) => ({ value: item.id, label: item.name }));
}
